import { RiskLevel } from '@equip.health/ui';
import { DateTime } from 'luxon';

import { GroupedAssessment } from '~/lib/types/survey/GroupedAssessment';

import { AssessmentChartDataScoreStrategyMap } from '../constants/survey/AssessmentChartDataScoreStrategyMap';
import { AssessmentListDataScoreStrategyMap } from '../constants/survey/AssessmentListDataScoreStrategyMap';
import { SurveyName } from '../constants/survey/SurveyName';
import { SurveyResponseStatus } from '../constants/survey/SurveyResponseStatus';
import { CSSRSurveyNamesToCheckForAlert } from '../constants/survey/SurveysToCheckForAlert';
import { AlertSurveys } from '../types/survey/AlertSurveys';
import {
  PatientAlert,
  PatientAlertAction,
} from '../types/patient/PatientAlert';

export const getLatestCssrsAssessment = (
  groupedAssessments: GroupedAssessment[],
): SurveyResponse | undefined => {
  const cssrsGroupedAssessments = groupedAssessments.filter((assessment) =>
    CSSRSurveyNamesToCheckForAlert.includes(assessment.name),
  );
  return cssrsGroupedAssessments
    .map((groupedAssessment) => groupedAssessment.assessments[0] || undefined)
    .filter((a) => a?.status === SurveyResponseStatus.DONE)
    .sort(
      (a1, a2) =>
        new Date(a2.updatedDate).getTime() - new Date(a1.updatedDate).getTime(),
    )[0];
};

export const getCssrsRiskLevel = (
  assessment: SurveyResponse,
  genderOnRecord: string,
) => {
  switch (assessment.surveyName) {
    case SurveyName.C_SSRS_LIFETIME: {
      return AssessmentListDataScoreStrategyMap[
        SurveyName.C_SSRS_LIFETIME
      ].deriveScores(assessment).riskCategories[1]; // second is the pastMonth risk, which is what we consider in the SR label
    }
    case SurveyName.C_SSRS_SLC_SCREENER: {
      return AssessmentListDataScoreStrategyMap[
        SurveyName.C_SSRS_SLC_SCREENER
      ].deriveScores(assessment).riskCategories[0];
    }
    case SurveyName.C_SSRS_SINCE_LAST_CONTACT: {
      return AssessmentChartDataScoreStrategyMap[
        SurveyName.C_SSRS_SINCE_LAST_CONTACT
      ].deriveScores(assessment, genderOnRecord).riskLevel;
    }
    default: {
      return undefined;
    }
  }
};

export const deriveSafetyPlanAlertFromCssrs = (
  groupedAssessments: GroupedAssessment[],
  patientName: string,
  genderOnRecord: string,
): AlertSurveys[] => {
  const latestCssrsAssessment = getLatestCssrsAssessment(groupedAssessments);
  const latestSafetyPlan = groupedAssessments.find(
    (ga) => ga.name === SurveyName.SAFETY_PLAN,
  )?.assessments[0];
  if (
    latestCssrsAssessment &&
    latestSafetyPlan?.status !== SurveyResponseStatus.DONE
  ) {
    const latestCssrsRiskLevel = getCssrsRiskLevel(
      latestCssrsAssessment,
      genderOnRecord,
    );
    if ([RiskLevel.MEDIUM, RiskLevel.HIGH].includes(latestCssrsRiskLevel)) {
      return [
        {
          action: SurveyName.SAFETY_PLAN,
          description: `${patientName} is high risk according to the C-SSRS submitted on
      ${DateTime.fromISO(latestCssrsAssessment.updatedDate).toFormat(
        'MM/dd/yyyy',
      )}, but does not have a finalized Safety Plan.`,
          name: latestCssrsAssessment.surveyName as SurveyName,
          responseExternalId: latestSafetyPlan
            ? latestSafetyPlan.responseExternalId
            : null,
          surveyExternalId: latestCssrsAssessment.surveyExternalId,
          title: latestSafetyPlan
            ? 'Safety plan incomplete'
            : 'Missing safety plan',
          updatedDate: latestCssrsAssessment.updatedDate,
        },
      ];
    }
  }
  return [];
};
export const deriveEmergencyContactAlert = (
  hasEmergencyContact: boolean,
  patientName: string,
): PatientAlert[] => {
  if (!hasEmergencyContact) {
    return [
      {
        action: PatientAlertAction.VIEW_PATIENT_PROFILE,
        title: 'Missing emergency contact',
        description: `${patientName} does not have an emergency contact listed. Please add an emergency contact to the Contact section of the chart.`,
        updatedDate: DateTime.now().toISO(),
      },
    ];
  }
  return [];
};
